import {provide, inject} from 'vue';
import {storeToRefs} from 'pinia';

// Symbol key for provide/inject
export const SURFACE_EDITOR_PHOTO_ACTIONS_KEY = Symbol('surface-editor-photo-actions');

/**
 * Called by the surface editor (index.vue) to provide the photo interaction API.
 * Any photos tab component (default or custom) can inject this to add photos
 * to the design surface via click and drag-and-drop.
 */
export function provideSurfaceEditorPhotoActions() {
  const surfaceEditorStore = useSurfaceEditorStore();
  const {activeSurface} = storeToRefs(surfaceEditorStore);
  const {buildPhotoElement} = useSurfaceEditorElementFactory();
  const {setDragPayload, clearDragPayload} = useSurfaceEditorDragState();

  function normalizeFile({file}) {
    let width = file.width;
    let height = file.height;
    const rotation = file.images.rotation;

    const query = {};

    if (rotation) {
      query.rotate = file.images.rotation;

      if ([90, 270].includes(rotation)) {
        width = file.height;
        height = file.width;
      }
    }

    return Object.assign({}, file, {
      width,
      height,
      images: {
        original: file.images?.original && updateQueryParameters(file.images.original, query),
        preview: file.images?.preview && updateQueryParameters(file.images.preview, query),
        rotation: file.images?.rotation
      }
    });
  }

  // Drag image element - created on pointerdown so it's ready before dragstart
  let dragImageEl = null;
  let dragPreviewUrl = null;

  function getPreviewUrl(file) {
    const normalizedFile = normalizeFile({file});
    const rotation = ((normalizedFile.images?.rotation || 0) % 360 + 360) % 360;

    if (normalizedFile.is?.gif) {
      return updateQueryParameters(normalizedFile.images?.original, {
        height: null,
        quality: null,
        format: null
      });
    } else if (normalizedFile.images?.preview) {
      const size = 350;
      const dimensionQuery = rotation % 180
        ? {width: size, height: null}
        : {height: size, width: null};

      return updateQueryParameters(normalizedFile.images?.preview, {
        ...dimensionQuery,
        interlace: true,
        quality: 60,
        strip_exif: true,
        format: 'jpg',
        rotate: rotation
      });
    }

    return normalizedFile.images?.preview;
  }

  function onPointerDown(event, file) {
    // Pre-create the drag image element before dragstart fires
    // This ensures the image is loaded from cache and ready to use
    dragPreviewUrl = getPreviewUrl(file);

    // Use a canvas for consistent drag image sizing across all browsers
    // Safari and Firefox ignore CSS dimensions on img elements for drag images
    const img = new Image();
    img.src = dragPreviewUrl;

    const createCanvas = () => {
      const minSize = 100;
      // Scale so the shorter dimension is 100px, preserving aspect ratio
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const width = aspectRatio >= 1 ? Math.round(minSize * aspectRatio) : minSize;
      const height = aspectRatio >= 1 ? minSize : Math.round(minSize / aspectRatio);

      dragImageEl = document.createElement('canvas');
      dragImageEl.width = width;
      dragImageEl.height = height;
      dragImageEl.style.cssText = 'position: fixed; top: -1000px; left: -1000px; pointer-events: none;';
      document.body.appendChild(dragImageEl);

      const ctx = dragImageEl.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
    };

    // Draw immediately if cached, otherwise wait for load
    if (img.complete && img.naturalWidth) {
      createCanvas();
    } else {
      img.onload = createCanvas;
    }
  }

  function onDragStart(event, file) {
    const dataTransfer = event.dataTransfer;

    if (!dataTransfer) {
      return;
    }

    const normalizedFile = normalizeFile({file});
    const previewUrl = dragPreviewUrl || getPreviewUrl(file);

    const payload = {
      type: 'photo',
      id: normalizedFile.id,
      name: normalizedFile.name,
      images: {
        original: normalizedFile.images?.original,
        preview: previewUrl || normalizedFile.images?.preview
      },
      width: normalizedFile.width,
      height: normalizedFile.height
    };

    dataTransfer.setData('application/json', JSON.stringify(payload));
    dataTransfer.setData('text/plain', normalizedFile.id);
    dataTransfer.effectAllowed = 'copy';

    // Use the pre-created drag image element, centered
    if (dragImageEl) {
      dataTransfer.setDragImage(dragImageEl, dragImageEl.width / 2, dragImageEl.height / 2);
    }

    setDragPayload(payload);
  }

  function onDragEnd() {
    clearDragPayload();

    if (dragImageEl) {
      dragImageEl.remove();
      dragImageEl = null;
    }
    dragPreviewUrl = null;
  }

  function addImage(file) {
    const newElement = buildPhotoElement({
      file: normalizeFile({file}),
      surface: activeSurface.value
    });

    if (!newElement) {
      return;
    }

    surfaceEditorStore.addElement({element: newElement, surface: activeSurface.value});
  }

  const api = {addImage, onPointerDown, onDragStart, onDragEnd};
  provide(SURFACE_EDITOR_PHOTO_ACTIONS_KEY, api);
  return api;
}

/**
 * Called by any photos tab component to consume the photo interaction API.
 * Must be used inside a component that is a descendant of the surface editor.
 */
export function useSurfaceEditorPhotoActions() {
  const api = inject(SURFACE_EDITOR_PHOTO_ACTIONS_KEY);
  if (!api) {
    throw new Error('useSurfaceEditorPhotoActions() must be used inside a surface editor');
  }
  return api;
}
